import { ProspectTermStatus } from '../constants/prospects'
import { enumNarrow } from '../helpers/enumGuard'
import { IARAPReconcileSummary, OngoingReportingType, Category } from './bbc'
import { IFile, IGroupedFile } from './box'
import { IClientInfo, IClientIntakeInfo } from './client'
import type { CompanyLifecycleStatus, CompanyLifecycleSubStatus } from './companyLifecycle'
import type { DealOutcome, DealStage } from './deal'
import type { OpsWriteupStatus } from './generalAnalysis'
import type { ReferralCounterpartyKind } from './overview'
import { IUser } from './user'

export interface IProspect extends IClientInfo {
  clientName: string
  lastDealActivityDate?: string
  arAmount?: string
  estimatedAvailability?: string
  inventoryAmount?: string
  netRevenue?: string
  applicationsDocuments?: {
    [key: string]: {
      type: string
      link: string
      files: IFile[]
    }
  }
  riskRating: number
  availableAR?: number
  availableInventory?: number
}

/**
 * Prospect-list row DTO, assembled from CRM data: client_info SF ids, the version's
 * deal, company.owner and the ops_reporting box link.
 */
export interface IProspectListRow {
  id: string
  name: string | null
  clientName: string
  clientInfo?: IProspect
  opsReporting?: IOPSReporting
  /** The stage of the version's deal while it is open. */
  dealStage: DealStage | null
  lastActivityDate: string | null
  commitmentAmount: number | null
  closeDate?: string | null
  shortDescription?: string | null
  industry?: string | null
  referralSource?: IProspectContactRef | null
  primaryContact?: IProspectContactRef | null
  dealSummaryStatus?: OpsWriteupStatus | null
  reportProgress?: IProspectReportProgress | null
  nextSteps: string | null
  companyId?: string | null
  dealId?: string | null
  ownerId: string | null
  riskRating: number
  user?: IUser
  // The CRM company's BDO, present only when it disagrees with the owner of the row's
  // open deal (`user`). Null whenever they agree.
  companyOwner?: IUser | null
}

/** A person, company or organization shown as a name card on a prospect row. */
export interface IProspectContactRef {
  id: string
  name: string
  kind: ReferralCounterpartyKind
  /** A person's company or organization, shown under their name. */
  companyName?: string | null
}

/** Analysis reports processed for a version: e.g. 3 of 5, with each report's state. */
export interface IProspectReportProgress {
  processed: number
  total: number
  reports: { label: string; status: OPSReportingFlowStatus }[]
}

/** How the prospect table is sectioned: by stage (Pipeline) or by BDO (early ladder). */
export type ProspectGroupBy = 'stage' | 'owner'

export interface IProspectsAggregation {
  data: IProspectListRow[]
  totalItems: number
  /** Grouped views only: the row cap cut into the last section, so its totals are a floor. */
  isLastGroupPartial?: boolean
  /** The Prospect filter's options. */
  prospects: Pick<IProspect, 'clientName'>[]
  users: IUser[]
  updatedAt: Date | string
}

interface IDealTally {
  count: number
  amount: number
}

/** Days without a CRM touch before an open deal counts as stale. */
export const STALE_DEAL_DAYS = 14

/** The prospects dashboard's figures; see server/src/services/prospects/pipelineMetrics. */
export interface IProspectPipelineMetrics {
  pipeline: IDealTally
  termSheets: number
  diligence: IDealTally
  closingThisQuarter: IDealTally
  closedYtd: IDealTally
  winRate: { won: number; lost: number }
  staleDeals: number
}
export enum Feedback {
  LIKED = 'LIKED',
  DISLIKED = 'DISLIKED',
  CLEAR = 'CLEAR',
}

export interface IFlagFeedback {
  id: string
  userId: string
  user?: IUser
  feedback: Feedback
}

export interface IProspectReportDocumentType {
  type: string
  title: string
  description: string
  isShortList?: boolean
}

export interface IProspectReportDocumentTypeClientIntake {
  type: string
  title: string
  description: string
  helperText?: string
  sampleLink?: string
  isShortList?: boolean
}

export const PROSPECT_REPORT_DOCUMENTS_TYPES_LIST: IProspectReportDocumentType[] = [
  {
    type: OngoingReportingType.BalanceSheet,
    title: 'Historical P&L and Balance Sheet',
    description: 'Monthly: Past 2 years and current year YTD',
  },
  {
    type: OngoingReportingType.BalanceSheetProjections,
    title: 'Forecasted P&L and Balance Sheet',
    description: 'Monthly',
    isShortList: true,
  },
  {
    type: OngoingReportingType.AR,
    title: 'AR Aging detail report',
    description: 'Detailed report at invoice level',
  },
  {
    type: OngoingReportingType.AP,
    title: 'AP Aging detail report',
    description: 'Detailed report at invoice level',
  },
  {
    type: OngoingReportingType.Inventory,
    title: 'Detailed Inventory Perpetual Report',
    description: 'Count & cost by SKU. Aging as well, if readily available',
    isShortList: true,
  },
  {
    type: OngoingReportingType.CapTable,
    title: 'Detailed cap table',
    description: 'This document is required for your application',
    isShortList: true,
  },
  {
    type: OngoingReportingType.OrgChart,
    title: 'Org chart including board members',
    description: 'A typed list with name and title is fine',
    isShortList: true,
  },
]

export const PROSPECT_REPORT_DOCUMENTS_TYPES_LIST_CLIENT_INTAKE: IProspectReportDocumentTypeClientIntake[] =
  [
    {
      type: OngoingReportingType.BalanceSheet,
      title: 'Financials',
      description: 'Monthly: Past 2 years and current year YTD',
      helperText: 'Upload at least 24 months of monthly Profit & Loss and Balance Sheet data.',
    },
    {
      type: OngoingReportingType.BalanceSheetProjections,
      title: 'Projections',
      helperText:
        'Upload your most recent monthly forecast for both Profit & Loss and Balance Sheet.',
      description: 'Monthly',
      isShortList: true,
    },
    {
      type: OngoingReportingType.AR,
      title: 'Accounts Receivable',
      helperText: 'Upload your latest AR aging report with customer-level detail.',
      description: 'Detailed report at invoice level',
      sampleLink: 'https://dwightfunding.app.box.com/s/7frg5758j34oysrebygpct6h3h2tug5f',
    },
    {
      type: OngoingReportingType.AP,
      title: 'Accounts Payable',
      helperText: 'Upload your latest AP aging report with vendor-level detail.',
      description: 'Detailed report at invoice level',
      sampleLink: 'https://dwightfunding.app.box.com/s/3eq34iyig0uf6bcl93xzg76j5afnj0zr',
    },
    {
      type: OngoingReportingType.Inventory,
      title: 'Inventory Report',
      description: 'Count & cost by SKU. Aging as well, if readily available',
      helperText:
        'Upload a detailed inventory aging report that includes: SKU, product description, inventory type (finished goods, work-in-process, raw materials), quantity (on-hand), and either average unit cost or total value',
      isShortList: true,
      sampleLink: 'https://dwightfunding.app.box.com/s/3wxl3dsvv9t079rkp8ldw114sirbom6k',
    },
    {
      type: OngoingReportingType.CapTable,
      title: 'Cap Table',
      description: 'This document is required for your application',
      helperText:
        'Provide a detailed cap table including all equity holders, instruments, and fully diluted ownership.',
      isShortList: true,
    },
    {
      type: OngoingReportingType.OrgChart,
      title: 'Org Chart',
      description: 'A typed list with name and title is fine',
      helperText:
        'Please provide your latest org chart, including names and titles of key team members.',
      isShortList: true,
    },
  ]

export const INTAKE_CATEGORY_BY_FILE_TYPE: Partial<Record<OngoingReportingType, string>> = {
  [OngoingReportingType.BalanceSheet]: 'Financials',
  [OngoingReportingType.BalanceSheetProjections]: 'Projections',
  [OngoingReportingType.AR]: 'Accounts Receivable',
  [OngoingReportingType.AP]: 'Accounts Payable',
  [OngoingReportingType.Inventory]: 'Inventory Report',
  [OngoingReportingType.CapTable]: 'Cap Table',
  [OngoingReportingType.OrgChart]: 'Org Chart',
  [OngoingReportingType.Miscellaneous]: 'Miscellaneous',
  [OngoingReportingType.Uncategorized]: 'Uncategorized',
}

export const INTAKE_CATEGORY_ORDER: string[] = [
  'Financials',
  'Projections',
  'Accounts Receivable',
  'Accounts Payable',
  'Inventory Report',
  'Cap Table',
  'Org Chart',
  'Miscellaneous',
  'Uncategorized',
]

export enum OPSReportingStatus {
  NotStarted = 'Not Started',
  InProgress = 'In Progress',
  Complete = 'Complete',
  Archived = 'Archived',
}

export enum OPSReportingFlowStatus {
  NotStarted = 'Not Started',
  SelectFile = 'Select File',
  AIProcessing = 'AI Processing',
  Failed = 'Failed',
  ReadyToMap = 'Ready to Map',
  Complete = 'Complete',
  Waived = 'Waived',
}

export enum SalesforceTypes {
  Lead = 'Lead',
  Account = 'Account',
  Opportunity = 'Opportunity',
  Referral__c = 'Referral__c',
  Campaign = 'Campaign',
  OpportunityHistory = 'OpportunityHistory',
  Contact = 'Contact',
  CampaignMember = 'CampaignMember',
  CampaignMemberStatus = 'CampaignMemberStatus',
  Investor__c = 'Investor__c',
  Vendor_Relationship__c = 'Vendor_Relationship__c',
  Competitor_Committee_Current_Lender__c = 'Competitor_Committee_Current_Lender__c',
  Event = 'Event',
  EventRelation = 'EventRelation',
  // Chatter feed objects — used for Market News posts on the "Activity in the Last
  // Week" Salesforce report's Chatter feed (FeedItem = a Collaborate post, FeedComment
  // = a reply under a post). See the Arc system prompt's Market News section.
  FeedItem = 'FeedItem',
  FeedComment = 'FeedComment',
  // T&E expense logging. Create/update only — deliberately excluded from
  // DeleteSalesforceRecord's allow-list (see DeleteRecord.ts).
  Expense__c = 'Expense__c',
}

export interface SalesforceContact {
  Id: string
  FirstName: string
  LastName: string
  Email: string
  Phone: string
}
export interface IOPSReportingFilesMapping {
  [key: string]: {
    id?: string
    fileId?: string
    oldFileId?: string
    sheetName?: string
    processed?: boolean
    completed?: boolean
  }
}

/** Kept on the deal, apart from its Reason Lost, and never synced to Salesforce. */
export interface IDealLostNotes {
  notes: string | null
  updatedAt: string | null
  updatedByName: string | null
}

export interface IOPSReporting {
  id: string
  status: OPSReportingStatus
  recordDate: string
  clientName: string
  clientInfo?: IProspect
  notApplicableDocuments?: string[]
  filesMapping?: IOPSReportingFilesMapping
  arReconcileSummary: IARAPReconcileSummary
  apReconcileSummary: IARAPReconcileSummary
  files?: IFile[]
  groupedFiles?: IGroupedFile[]
  opsReportingFlows: IOPSReportingFlow[]
  preProcessed: boolean
  clientIntakeInfo?: IClientIntakeInfo
  clientIntakeSubmittedAt?: string
  opsBoxLink: string | null
  lastDealActivityDate: string | null
  createdAt?: string
  // The deal this version is the BD workspace for.
  dealId?: string
  /** The version's deal, where a list loads it (the Version dropdown's sizes). */
  deal?: { commitmentAmount: number | string | null } | null
  // Computed server-side from CRM data: the SF record link from client_info's ids, and
  // the stage, outcome and company lifecycle from the version's deal.
  salesforceId?: string | null
  salesforceType?: string | null
  salesforceLink?: string | null
  dealStage?: DealStage | null
  dealOutcome?: DealOutcome | null
  lifecycleStatus?: CompanyLifecycleStatus | null
  lifecycleSubStatus?: CompanyLifecycleSubStatus | null
  bdo?: IUser | null
  /** Set only while the version's deal is Lost or Passed. */
  lostDealNotes?: IDealLostNotes | null
  refreshedAt: string | null
  preprocessingSummaryMessage?: string
}

export interface IOPSReportingFlowFiles {
  id: string
  fileId: string | null
  file?: IFile
  sheetName: string | null
  data?: any
}

export interface IOPSReportingFlow {
  id: string
  type: OngoingReportingType
  status: OPSReportingFlowStatus
  boxLink?: string
  crossAge: number
  concentrationLimit: number
  advanceRate: number
  files: IOPSReportingFlowFiles[]
}

export interface IProspectReporting {
  id: string
  clientName: string
  clientId: string
  opsReportingId: string
  type: OngoingReportingType
  boxLink?: string
  submittedDate?: string
  monthsProcessed?: string
  status: OPSReportingFlowStatus
  createdAt: string
  updatedAt: string | null
  completedAt: string | null
  filesCount?: number
  preprocessingSummaryMessage?: string | null
  postProcessingAssessment?: string | null
}

export interface IProspectReportingAggregation {
  data: IProspectReporting[]
  totalCount: number
}

export const PROSPECT_REPORTING_MAP: Partial<Record<OngoingReportingType, string>> = {
  [OngoingReportingType.AR]: 'AR Aging',
  [OngoingReportingType.AP]: 'AP Aging',
  [OngoingReportingType.IncomeStatement]: 'Income Statement',
  [OngoingReportingType.BalanceSheet]: 'Balance Sheet',
  [OngoingReportingType.IncomeStatementProjections]: 'Projected Income Statement',
  [OngoingReportingType.BalanceSheetProjections]: 'Projected Balance Sheet',
  [OngoingReportingType.Inventory]: 'Inventory',
  [OngoingReportingType.CapTable]: 'Detailed Cap Table',
  [OngoingReportingType.OrgChart]: 'Org Chart',
  [OngoingReportingType.Uncategorized]: 'Uncategorized Files',
  [OngoingReportingType.Miscellaneous]: 'Miscellaneous',
}

export const PROSPECT_REPORTING_TYPES = [
  OngoingReportingType.AR,
  OngoingReportingType.AP,
  OngoingReportingType.Inventory,
  OngoingReportingType.BalanceSheet,
  OngoingReportingType.IncomeStatement,
  OngoingReportingType.BalanceSheetProjections,
  OngoingReportingType.IncomeStatementProjections,
]

/**
 * Report types stored as documents rather than parsed into data — the AI processing path
 * skips them.
 */
export const NON_AI_PROCESSED_REPORT_TYPES = [
  OngoingReportingType.CapTable,
  OngoingReportingType.OrgChart,
  OngoingReportingType.Miscellaneous,
]

export const PROSPECT_REPORTING_EXTENDED_TYPES = [
  ...PROSPECT_REPORTING_TYPES,
  OngoingReportingType.CapTable,
  OngoingReportingType.OrgChart,
]

export enum ProspectFolderNames {
  AR = 'Aging Report/AR',
  AP = 'Aging Report/AP',
  Inventory = 'Inventory',
  Financials = 'Financials',
  Projections = 'Projections',
  CapTable = 'Cap Table',
  Misc = 'Misc',
  SampleInvoice = 'Sample Invoice',
  TermSheet = 'Term Sheet',
  Uncategorized = 'Uncategorized',
}

export const NON_UPLOADABLE_PROSPECT_FOLDERS = [
  ProspectFolderNames.SampleInvoice,
  ProspectFolderNames.TermSheet,
  ProspectFolderNames.Misc,
  ProspectFolderNames.CapTable,
  ProspectFolderNames.Uncategorized,
]

export enum ProspectFolderTypes {
  Atlas = 'Atlas',
  Originals = 'Originals',
}

export const PROSPECT_FILES_FOLDERS_MAP: Partial<
  Record<OngoingReportingType, ProspectFolderNames>
> = {
  [OngoingReportingType.AR]: ProspectFolderNames.AR,
  [OngoingReportingType.AP]: ProspectFolderNames.AP,
  [OngoingReportingType.Inventory]: ProspectFolderNames.Inventory,
  [OngoingReportingType.BalanceSheet]: ProspectFolderNames.Financials,
  [OngoingReportingType.IncomeStatement]: ProspectFolderNames.Financials,
  [OngoingReportingType.BalanceSheetProjections]: ProspectFolderNames.Projections,
  [OngoingReportingType.IncomeStatementProjections]: ProspectFolderNames.Projections,
  [OngoingReportingType.CapTable]: ProspectFolderNames.CapTable,
  [OngoingReportingType.OrgChart]: ProspectFolderNames.CapTable,
  [OngoingReportingType.Miscellaneous]: ProspectFolderNames.Misc,
  [OngoingReportingType.Uncategorized]: ProspectFolderNames.Uncategorized,
}

export interface IProspectTerms {
  id: string
  clientName: string
  clientInfo?: IProspect
  recordDate: string
  status: ProspectTermStatus | null
  maxLineAmount: number
  minimumLineAmount: number
  minimumInterestEffectiveDate: string
  unusedLineFee: number
  interestRate: number
  interestRateBase: number
  interestRateAbovePrime: number
  loanType: string
  arAdvance: number
  inventoryAdvanceRateCost: number
  inventoryAdvanceRateNolv: number
  sublimit: number
  facilityFee: number
  terminationYear1: number
  terminationOngoing: number
  expirationDate: string
  deposits: number
  guarentees: string
  subordination: boolean
  commentsForCommittee: string
  covenantType: string
  qualifiedEquityRound: number
  createdAt: string
  updatedAt: string
  summaryOfChanges: string
  boxLink: string | null
  submittedProspectTermId?: string | null
  user?: IUser
}

export interface IProspectTermsSummaryInfo {
  data: IProspectTerms
  totals: {
    previous: IProspectTerms[]
  }
}

export interface IProspectSummaryInventory {
  numberOfSKUs: number
  inventoryValue: number
  averageCOGS: number
  dioInventory: number
  averageInventoryBS: number
  dioBS: number
}

export interface IProspectSummaryCashFlow {
  asOfDate: string
  numberOfMonthLast: number
  numberOfMonthNext: number
  mostRecentCash: number
  avgOperatingCashFlow: number
  monthsOfCashOnHandOperatingCashFlow: number | string
  monthsOfCashOnHandEBITDA: number | string
  avgEBITDA: number
  avgNetIncome: number
}

interface IProspectReportBuckets {
  _0_to_30_days: number
  _31_to_60_days: number
  _61_to_90_days: number
  _91_plus_days: number
  total: number
}

interface IProspectReportBase extends IProspectReportBuckets {
  id: string
  clientName: string
  total_percent: number
  entity_id: string | null
  prospect_entity_id: string | null
}

export interface IProspectAR extends IProspectReportBase {
  debtor: string | null
  cross_aged: number
  concentration_limit: number
  eligible_ar: number
  ineligible_ar: number
  available_ar: number
  eligible: boolean
  rows?: IProspectAR[]
  totalCount?: number
}

interface IProspectARTotals extends IProspectReportBuckets {
  cross_aged: number
  concentration_limit: number
  eligible_ar: number
  ineligible_ar: number
  available_ar: number
  eligibleCount: number
  totalCount: number
  totalItems: number
}
export interface IProspectARSummary {
  data: IProspectAR[]
  totals: IProspectARTotals
}

export interface IProspectAP extends IProspectReportBase {
  creditor: string | null
  rows: IProspectAP[]
  totalCount?: number
}

interface IProspectAPTotals extends IProspectReportBuckets {
  totalItems: number
}
export interface IProspectAPSummary {
  data: IProspectAP[]
  totals: IProspectAPTotals
}

export enum ProspectRefreshType {
  Codat = 'codat',
  Box = 'box',
  Uncategorized = 'uncategorized',
}

export const BD_REPORT_ORDER_DEFAULT = [
  OngoingReportingType.AR,
  OngoingReportingType.AP,
  OngoingReportingType.Inventory,
  OngoingReportingType.IncomeStatement,
  OngoingReportingType.BalanceSheet,
  OngoingReportingType.IncomeStatementProjections,
  OngoingReportingType.BalanceSheetProjections,
  OngoingReportingType.CapTable,
  OngoingReportingType.OrgChart,
]

export const BD_REPORT_ORDER = [
  OngoingReportingType.AP,
  OngoingReportingType.AR,
  OngoingReportingType.BalanceSheet,
  OngoingReportingType.IncomeStatement,
  OngoingReportingType.Inventory,
  OngoingReportingType.Miscellaneous,
  OngoingReportingType.BalanceSheetProjections,
  OngoingReportingType.IncomeStatementProjections,
  OngoingReportingType.Uncategorized,
]

export type ArEligibilityRow = {
  Debtor: string
  Eligible: string | boolean
  Category: Category
  'Credit Limits': string | number
  'Concentration Limit': string | number
  Notes: string | number
}

// Enhanced OPS Classification Interfaces

export interface ITabAnalysis {
  // For AR/AP Aging
  detail_level?: 'detailed' | 'summary' | 'unknown'
  required_columns_present?: string[]
  required_columns_missing?: string[]
  aging_buckets_present?: string[]
  aging_buckets_missing?: string[]

  // For Inventory
  inventory_columns_present?: string[]
  inventory_columns_missing?: string[]

  // For Financial Statements (Income Statement, Balance Sheet)
  date_range?: string
  months_covered?: number
  format_type?: 'monthly' | 'yearly' | 'point_in_time' | 'mixed' | 'unknown'
  meets_minimum_period?: boolean

  // For Projections
  projection_date_range?: string
  projection_months_covered?: number
  covers_current_year_end?: boolean

  // General
  data_quality_score?: number
  recommended_for_processing?: boolean
}

export enum ProcessingType {
  Reader = 'reader',
  AIProcessor = 'ai_processor',
  Unprocessable = 'unprocessable',
  Verified = 'verified',
}

export interface ISingleFileClassification {
  file_id: string
  processable_tab_names: string[]
  report_type: OngoingReportingType[]
  processing_type: ProcessingType
  notes: string
  report_date_start: string
  report_date_end: string
  is_data_incomplete: boolean
}

export interface IProcessingCandidate {
  file_id: string
  tab_name: string
  report_type: OngoingReportingType
  confidence_score: number
  why_recommended: string
}

export interface IFileOption {
  file_id: string
  tab_name: string
  processing_ready: boolean
  quality_issues?: string[]
}

export interface IRequirementStatus {
  meets_column_requirements?: boolean
  meets_time_period_requirements?: boolean
  meets_format_requirements?: boolean
  specific_gaps?: string[]
}

export interface IReportClassification {
  report_type: OngoingReportingType
  best_candidate?: IProcessingCandidate
  all_options: IFileOption[]
  requirement_status: IRequirementStatus
  ai_processing_details?: {
    ai_processing_necessary?: boolean
    ai_proccessing_notes?: string
    ai_processing_files?: Array<{
      file_id: string
      tab_name: string
      notes: string
    }>
  }
}

export interface IAnalysisSummary {
  files_ready_for_processing: IProcessingCandidate[]
  requirements_met: string[]
  requirements_missing: string[]
  overall_status: 'Complete' | 'Partial' | 'Incomplete'
  processing_recommendations: string[]
  critical_gaps: string[]
  notes: string
}

export interface IComprehensiveDealAssessment {
  analysis_summary: IAnalysisSummary
  report_classifications: IReportClassification[]
}

export interface IPostProcessingAssessment {
  success: boolean
  rowsProcessed: number
  rowsLoaded: number
  rowsFailed: number
  totals?: {
    totalFromReport?: number
    totalCalculated?: number
    [key: string]: number | undefined
  }
  errors: string[]
  warnings: string[]
  qualityScore?: number
  processingDuration?: number
  timestamp: string
}

/** Cast-free narrower for SalesforceTypes (see common/helpers/enumGuard). */
export const asSalesforceType = enumNarrow(SalesforceTypes).as
